import { useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { getTenantById } from '@/services/tenantService';
import { useAuthStore } from '@/stores/authStore';

export const Route = createFileRoute('/t/')({
  component: TenantIndexRedirect,
});

/**
 * Initial landing redirect only — tenant freely navigates after this.
 * CONTRACT_REVIEW_PENDING → /t/contracts, otherwise → /t/employees.
 *
 * Session status is frozen at login per BE, so when it stamps ONBOARDING we
 * still need a live read to handle the refresh-on-/t/-post-activation case.
 * Same query key as `/t.tsx` so the two calls dedupe to a single network read.
 */
function TenantIndexRedirect() {
  const navigate = useNavigate();
  const sessionTenantStatus = useAuthStore((s) => s.session?.tenant.status ?? null);
  const tenantId = useAuthStore((s) => s.session?.tenant.id ?? '');

  const { data: liveTenant } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => getTenantById(tenantId),
    enabled: !!tenantId && sessionTenantStatus === 'ONBOARDING',
    staleTime: 30_000,
  });

  const effectiveStatus =
    sessionTenantStatus === 'ONBOARDING' && liveTenant
      ? liveTenant.tenant.status
      : sessionTenantStatus;

  useEffect(() => {
    if (!effectiveStatus) return;
    // ONBOARDING is handled by the parent /t.tsx layout (redirects to /requested-details).
    if (effectiveStatus === 'ONBOARDING') return;
    const target =
      effectiveStatus === 'CONTRACT_REVIEW_PENDING' ? '/t/contracts' : '/t/employees';
    void navigate({ to: target, replace: true });
  }, [effectiveStatus, navigate]);

  return null;
}
